import {
  Paper,
  Grid,
  Typography,
  Box,
  AccordionDetails,
  SvgIcon,
  Menu,
  MenuItem,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as Video } from 'assets/images/video_icon.svg';
import { ReactComponent as VideoDark } from 'assets/images/video_icon_dark.svg';
import { ReactComponent as Question } from 'assets/images/question_training.svg';
import { ReactComponent as QuestionDark } from 'assets/images/question_training_dark.svg';
import { ReactComponent as VideoLarge } from 'assets/images/video_icon_large.svg';
import { ReactComponent as QuestionLarge } from 'assets/images/question_training_large.svg';
import { ReactComponent as ArrowBottom } from 'assets/images/arrow-bottom.svg';
import { ReactComponent as DeleteQuestion } from 'assets/images/delete-item-training.svg';
import { ReactComponent as CheckTraining } from 'assets/images/check-training.svg';
import { ReactComponent as UncheckTraining } from 'assets/images/uncheck-training.svg';
import { ReactComponent as SettingsQuiz } from 'assets/images/settings-quiz.svg';
import { ReactComponent as SwitchActive } from 'assets/images/Switch_button.svg';
import { ReactComponent as SwitchNotActive } from 'assets/images/Switch_button_disable.svg';
import { ReactComponent as AddStep } from 'assets/images/Token_button.svg';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import {
  AppFormikTextArea,
  AppFormikTextField,
  AppFormikTextFieldNumber,
  AppPrimaryButton,
  YouTubePreviewComponent,
} from 'shared/components/AppComponents';
import MuiAccordion from '@mui/material/Accordion';
import { styled } from '@mui/material/styles';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import { useFormikContext, getIn } from 'formik';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { extractYouTubeId } from 'shared/helpers/ExtractYoutubeId';
import ReactQuill from 'react-quill-new';

interface RiskManagementFormProps {
  test: any;
  trainingDefinition?: any;
  errorMessages?: any;
}

const Accordion = styled((props: any) => (
  <MuiAccordion
    disableGutters
    elevation={0}
    square
    sx={{ border: `1px solid ${props.customBorder ? '#D1F27A' : '#C2C2C2'}` }}
    {...props}
  />
))(() => ({
  borderRadius: '5px',
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ArrowBottom />} {...props} />
))(({ theme }) => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const TrainingSteps: React.FC<RiskManagementFormProps> = (props: any) => {
  const trainingStepsRef = useRef<HTMLDivElement | null>(null);
  const { trainingDefinition, errorMessages } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElQuestion, setAnchorElQuestion] = useState<null | HTMLElement>(
    null
  );
  const [stepHover, setStepHover] = useState<any>(null);
  const [activeStep, setActiveStep] = useState<any>(
    trainingDefinition && trainingDefinition.length > 0
      ? { ...trainingDefinition[0], index: 0 }
      : { index: 0, type: undefined }
  );
  const [expandedQuestion, setExpandedQuestion] = useState<string>('0');
  const { values, setFieldValue, errors, submitCount } =
    useFormikContext<any>();

  const activeStepIndex =
    values.training_definition &&
    values.training_definition?.findIndex((s: any) => s.id === activeStep?.id);
  const settingsPath = `training_definition[${activeStepIndex}].settings`;
  const settings = values.training_definition?.[activeStepIndex]?.settings;

  const fieldName = `training_definition[${activeStepIndex}].youtubeUrl`;
  const currentUrl = getIn(values, fieldName);
  const [videoId, setVideoId] = useState<string | null>(() =>
    extractYouTubeId(currentUrl ?? '')
  );
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(() => {
    if (!currentUrl) return false;
    return !extractYouTubeId(currentUrl);
  });

  const handleClickAddQuestion = (event: any): void => {
    setAnchorElQuestion(event.currentTarget);
  };
  const handleCloseAddQuestion = (): void => {
    setAnchorElQuestion(null);
  };

  const handleAddQuestion = (data: string): void => {
    const stepPath = `training_definition[${activeStepIndex}]`;
    const currentQuestions =
      values.training_definition?.[activeStepIndex]?.questions ?? [];
    const defaultData = {
      id: crypto.randomUUID(),
      type: data,
      prompt: '',
      explanation: '',
    };

    const newQuestion =
      data === 'multiple_choice'
        ? {
            ...defaultData,
            options: [{ id: crypto.randomUUID(), text: '', isCorrect: false }],
          }
        : {
            ...defaultData,
            correctAnswer: false,
          };

    setFieldValue(`${stepPath}.questions`, [...currentQuestions, newQuestion]);
    setExpandedQuestion(String(currentQuestions.length));
    handleCloseAddQuestion();
  };

  const open = Boolean(anchorEl);
  const openQuestion = Boolean(anchorElQuestion);

  const handleClickAddStep = (event: any): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseAddStep = (): void => {
    setAnchorEl(null);
  };

  const handleYouTubeUrlChange = useCallback(
    (e: any) => {
      const url = e.target.value;
      setFieldValue(`training_definition[${activeStepIndex}].youtubeUrl`, url);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      setPreviewError(false);
      setVideoId(null);

      if (!url.trim()) {
        setPreviewLoading(false);
        return;
      }

      setPreviewLoading(true);

      debounceTimer.current = setTimeout(() => {
        const id = extractYouTubeId(url);
        if (id) {
          setVideoId(id);
          setPreviewError(false);
        } else {
          setVideoId(null);
          setPreviewError(true);
        }
        setPreviewLoading(false);
      }, 600);
    },
    [setFieldValue, activeStepIndex]
  );

  const handleAddStep = (type: 'video' | 'quiz' | 'text'): void => {
    const newStep: any = {
      id: crypto.randomUUID(),
      type,
      title: '',
    };

    if (type === 'quiz') {
      newStep.questions = [];
      newStep.settings = {
        passingScore: 0,
        attemptsLimit: 'unlimited',
        randomizeAnswers: false,
        showExplanations: true,
        showCorrectAnswers: true,
      };
    }

    if (type === 'text') {
      newStep.content = '';
    }

    if (type !== 'quiz' && type !== 'text') {
      newStep.youtubeUrl = '';
    }

    setFieldValue('training_definition', [
      ...values.training_definition,
      newStep,
    ]);
    handleCloseAddStep();
  };

  const handleDeleteStep = (stepId: string): void => {
    const updated = values.training_definition.filter(
      (s: any) => s.id !== stepId
    );
    setFieldValue('training_definition', updated);
    if (activeStep?.id === stepId) setActiveStep(updated[0] ?? null);
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    const url = getIn(values, fieldName) ?? '';

    if (!url) {
      setVideoId(null);
      setPreviewError(false);
      setPreviewLoading(false);
      return;
    }

    const id = extractYouTubeId(url);
    setVideoId(id);
    setPreviewError(!id);
    setPreviewLoading(false);
  }, [activeStepIndex]);

  useEffect(() => {
    if (submitCount === 0) return;
    if (!errors || Object.keys(errors).length === 0) return;

    if (Object.keys(errors || {}).includes('training_definition')) {
      trainingStepsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [errors, submitCount]);

  return (
    <Paper
      variant="outlined"
      className="training-steps-wrapper"
      sx={{
        border: errorMessages ? '1px solid #d32f2f' : '',
        borderWidth: errorMessages ? 'thin !important' : '',
      }}
      ref={trainingStepsRef}
    >
      <Typography variant="h6">
        Training steps*{' '}
        <span
          style={{
            color: '#d32f2f',
            fontSize: '10px',
            fontWeight: 400,
          }}
        >
          {errorMessages ? errorMessages : ''}
        </span>
      </Typography>
      <Grid container className="training-steps-grid">
        <Grid item className="training-steps-grid-1" xs={12} md={4}>
          <Box sx={{ padding: '10px' }}>
            {values &&
              values.training_definition?.map((step: any, index: number) => {
                const isActive = activeStep?.id === step.id;
                const isVideo = step.type === 'video';
                const stepFormikIndex = values.training_definition?.findIndex(
                  (s: any) => s.id === step.id
                );
                const questionCount =
                  values.training_definition?.[stepFormikIndex]?.questions
                    ?.length ?? 0;
                const isURLSet =
                  values.training_definition?.[stepFormikIndex]?.youtubeUrl;

                return (
                  <Box
                    key={step.id}
                    onClick={() => setActiveStep({ ...step, index })}
                    className="training-steps-item-list"
                    sx={{
                      background: isActive ? '#052921' : '#f3f4f6',
                      color: isActive ? '#fff' : '#111',
                    }}
                    onMouseEnter={() => setStepHover(index)}
                    onMouseLeave={() => setStepHover(null)}
                  >
                    <Box sx={{ display: 'flex', gap: '5px' }}>
                      <Box sx={{ fontWeight: 600 }}>{index + 1}.</Box>
                      <Box sx={{ flex: 1 }}>
                        <input
                          value={step.title}
                          className="training-item-title"
                          style={{
                            color: isActive ? 'white' : '#3D3C42',
                            fontWeight: 500,
                          }}
                          readOnly
                        />
                      </Box>
                      {stepHover === index && (
                        <HighlightOffIcon
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteStep(step.id);
                          }}
                          sx={{
                            color: '#DE3A3A',
                          }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: 6,
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        {isActive && isVideo && <Video />}
                        {!isActive && isVideo && <VideoDark />}
                        {isActive && !isVideo && <Question />}
                        {!isActive && !isVideo && <QuestionDark />}
                        <Box
                          sx={{
                            background: isActive ? '#D1F27A' : '#3D3C42',
                            color: isActive ? '#3D3C42' : 'white',
                            padding: '2px 8px',
                            borderRadius: 20,
                            fontSize: 12,
                          }}
                        >
                          {step.type.charAt(0).toUpperCase() +
                            step.type.slice(1)}
                        </Box>
                      </Box>

                      <Box>
                        {isVideo && (
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: isActive ? '#D1F27A' : '#3D3C42',
                            }}
                          >
                            {isURLSet && 'YouTube URL set'}
                          </Typography>
                        )}
                        {step.type === 'quiz' && (
                          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
                            {questionCount} questions
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}

            <Box sx={{ cursor: 'pointer' }} onClick={handleClickAddStep}>
              <AddStep />
            </Box>

            <Menu
              id="add-step-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleCloseAddStep}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem onClick={() => handleAddStep('video')}>Video</MenuItem>
              <MenuItem onClick={() => handleAddStep('quiz')}>Quiz</MenuItem>
              <MenuItem onClick={() => handleAddStep('text')}>Text</MenuItem>
            </Menu>
          </Box>
        </Grid>

        <Grid item className="training-steps-grid-2" xs={12} md={8}>
          {/* VIDEO */}
          {activeStep && activeStep.type === 'video' && (
            <Box sx={{ padding: '10px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  paddingBottom: '20px',
                  borderBottom: '1px solid #D1F27A',
                }}
              >
                <VideoLarge />
                <Box>
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                      Step {parseFloat(activeStep.index) + 1}
                    </Typography>
                    <Box
                      sx={{
                        background: '#052921',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: 20,
                        fontSize: 12,
                      }}
                    >
                      Video
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 14, color: '#3D3C42' }}>
                    {activeStep.title}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ paddingTop: '20px' }}>
                <AppFormikTextField
                  name={`training_definition[${activeStepIndex}].title`}
                  placeholder={activeStep.title || 'Enter title'}
                  outerLabel
                  label="Step title (optional)"
                />
                <AppFormikTextField
                  name={`training_definition[${activeStepIndex}].youtubeUrl`}
                  placeholder="https://youtu.be/example"
                  outerLabel
                  label="YouTube URL"
                  onChange={handleYouTubeUrlChange}
                />
                <Typography sx={{ color: '#A3A3A3', fontSize: '12px' }}>
                  Paste a YouTube video URL. Supported formats:
                  youtube.com/watch?v=..., youtu.be/..., youtube.com/embed/...
                </Typography>
                <Typography
                  variant="body1"
                  className="formik-text-field-container"
                  id="textfield-typography"
                  sx={{ marginTop: '10px' }}
                >
                  Preview
                </Typography>

                <Box>
                  <YouTubePreviewComponent
                    videoId={videoId}
                    previewLoading={previewLoading}
                    previewError={previewError}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* QUIZ */}
          {activeStep && activeStep.type === 'quiz' && (
            <Box sx={{ padding: '10px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  paddingBottom: '20px',
                  borderBottom: '1px solid #D1F27A',
                }}
              >
                <QuestionLarge />
                <Box>
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                      Step {parseFloat(activeStep.index) + 1}
                    </Typography>
                    <Box
                      sx={{
                        background: '#052921',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: 20,
                        fontSize: 12,
                      }}
                    >
                      Quiz
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 14, color: '#3D3C42' }}>
                    {activeStep.title}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ paddingTop: '20px' }}>
                <AppFormikTextField
                  name={`training_definition[${activeStepIndex}].title`}
                  placeholder={activeStep.title}
                  outerLabel
                  label="Quiz title (optional)"
                />
              </Box>

              {values.training_definition?.[activeStepIndex]?.questions?.map(
                (questionData: any, questionIndex: number) => {
                  const questionPath = `training_definition[${activeStepIndex}].questions[${questionIndex}]`;
                  const isTrueFalse = questionData.type === 'true_false';
                  const questionKey = String(questionIndex);

                  return (
                    <Box key={questionData.id} sx={{ marginBottom: '10px' }}>
                      <Accordion
                        expanded={expandedQuestion === questionKey}
                        onChange={() =>
                          setExpandedQuestion(
                            expandedQuestion === questionKey ? '' : questionKey
                          )
                        }
                        customBorder={expandedQuestion === questionKey}
                      >
                        <AccordionSummary
                          aria-controls={`panel${questionKey}-content`}
                          id={`panel${questionKey}-header`}
                        >
                          <Box sx={{ display: 'flex', width: '100%' }}>
                            <Typography sx={{ fontWeight: 600, width: '100%' }}>
                              Question {questionIndex + 1} -{' '}
                              <span
                                style={{
                                  fontSize: '14px',
                                  color: '#7E7E7E',
                                  fontWeight: 'normal',
                                }}
                              >
                                {questionData.prompt}
                              </span>
                            </Typography>
                            <DeleteQuestion
                              onClick={() => {
                                const updatedQuestions =
                                  values.training_definition[
                                    activeStepIndex
                                  ].questions.filter(
                                    (_: any, i: number) => i !== questionIndex
                                  );
                                setFieldValue(
                                  `training_definition[${activeStepIndex}].questions`,
                                  updatedQuestions
                                );
                              }}
                            />
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails>
                          <Typography>
                            <AppFormikTextField
                              name={`${questionPath}.prompt`}
                              placeholder={questionData.prompt}
                              outerLabel
                              label="Question prompt"
                            />
                          </Typography>

                          <Typography
                            variant="body1"
                            className="formik-text-field-container"
                            id="textfield-typography"
                            sx={{ marginTop: '10px' }}
                          >
                            Answer options (Assign the correct answer)
                          </Typography>

                          {!isTrueFalse &&
                            questionData.options?.map(
                              (option: any, optionIndex: number) => (
                                <Box
                                  key={option.id}
                                  sx={{
                                    display: 'flex',
                                    gap: '10px',
                                    alignItems: 'center',
                                    marginTop: '10px',
                                  }}
                                >
                                  <Box
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() =>
                                      setFieldValue(
                                        `${questionPath}.options[${optionIndex}].isCorrect`,
                                        !option.isCorrect
                                      )
                                    }
                                  >
                                    {option.isCorrect ? (
                                      <CheckTraining />
                                    ) : (
                                      <UncheckTraining />
                                    )}
                                  </Box>
                                  <AppFormikTextField
                                    name={`${questionPath}.options[${optionIndex}].text`}
                                    placeholder={option.text}
                                    sx={{
                                      border: '1px solid #D1F27A',
                                      p: '10px',
                                      borderRadius: '5px',
                                      width: '100%',
                                    }}
                                  />
                                  <Box sx={{ cursor: 'pointer' }}>
                                    <img
                                      src="/images/icons/delete-answer.png"
                                      onClick={() => {
                                        const updatedOptions =
                                          questionData.options.filter(
                                            (_: any, i: number) =>
                                              i !== optionIndex
                                          );
                                        setFieldValue(
                                          `${questionPath}.options`,
                                          updatedOptions
                                        );
                                      }}
                                    />
                                  </Box>
                                </Box>
                              )
                            )}

                          {isTrueFalse && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                margin: '20px 0',
                              }}
                            >
                              <Typography>
                                {questionData.correctAnswer ? 'True' : 'False'}
                              </Typography>
                              <Box
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                  setFieldValue(
                                    `${questionPath}.correctAnswer`,
                                    !questionData.correctAnswer
                                  )
                                }
                              >
                                {questionData.correctAnswer ? (
                                  <SwitchActive />
                                ) : (
                                  <SwitchNotActive />
                                )}
                              </Box>
                            </Box>
                          )}

                          {!isTrueFalse && (
                            <AppPrimaryButton
                              onClick={() => {
                                const currentOptions =
                                  questionData.options ?? [];
                                setFieldValue(`${questionPath}.options`, [
                                  ...currentOptions,
                                  {
                                    id: crypto.randomUUID(),
                                    text: '',
                                    isCorrect: false,
                                  },
                                ]);
                              }}
                              className="btn-small"
                              title="Add option"
                              startIcon={
                                <SvgIcon
                                  component={Plus}
                                  inheritViewBox
                                  className="view-icon"
                                  fontSize="inherit"
                                  htmlColor="#76A81F"
                                />
                              }
                              sx={{ margin: '10px 0' }}
                            />
                          )}

                          <Box
                            sx={{
                              borderTop: '1px solid #D1F27A',
                              paddingTop: '10px',
                            }}
                          >
                            <AppFormikTextArea
                              name={`${questionPath}.explanation`}
                              placeholder={questionData.explanation}
                              label="Explanation (optional)"
                              outerLabel
                              rows={3}
                            />
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  );
                }
              )}

              <Menu
                id="add-question-menu"
                anchorEl={anchorElQuestion}
                open={openQuestion}
                onClose={handleCloseAddQuestion}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                <MenuItem onClick={() => handleAddQuestion('multiple_choice')}>
                  Multiple choice
                </MenuItem>
                <MenuItem onClick={() => handleAddQuestion('true_false')}>
                  True / False
                </MenuItem>
              </Menu>

              <AppPrimaryButton
                onClick={handleClickAddQuestion}
                className="btn-small"
                title="Add question"
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                    htmlColor="#76A81F"
                  />
                }
                sx={{ margin: '0 0 10px 0' }}
              />

              <Typography
                variant="body1"
                className="formik-text-field-container"
                id="textfield-typography"
                sx={{ marginTop: '10px', fontSize: '14px' }}
              >
                Quiz Settings
              </Typography>
              <Box
                sx={{
                  border: '1px solid #C2C2C2',
                  marginTop: '10px',
                  borderRadius: '5px',
                  padding: '10px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    color: '#3D3C42',
                    gap: '10px',
                    alignItems: 'center',
                    borderBottom: '1px solid #D1F27A',
                    paddingBottom: '20px',
                  }}
                >
                  <SettingsQuiz />
                  <Typography sx={{ fontSize: '16px' }}>
                    Quiz Settings
                  </Typography>
                </Box>

                {/* Attempts */}
                <Box
                  sx={{
                    background: '#F1F5F46B',
                    padding: '5px',
                    borderRadius: '5px',
                    marginTop: '10px',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#3D3C42',
                      fontSize: '15px',
                      fontWeight: 600,
                    }}
                  >
                    Attempts Limit
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      padding: '10px 0',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      setFieldValue(
                        `${settingsPath}.attemptsLimit`,
                        'unlimited'
                      )
                    }
                  >
                    {settings?.attemptsLimit === 'unlimited' ? (
                      <CheckTraining />
                    ) : (
                      <UncheckTraining />
                    )}
                    <Typography>Unlimited attempts</Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      setFieldValue(`${settingsPath}.attemptsLimit`, 'fixed')
                    }
                  >
                    {settings?.attemptsLimit === 'fixed' ? (
                      <CheckTraining />
                    ) : (
                      <UncheckTraining />
                    )}
                    <Typography>Fixed number</Typography>
                  </Box>
                </Box>

                {/* Required Passing Score */}
                <Box
                  sx={{
                    background: '#F1F5F46B',
                    padding: '5px',
                    borderRadius: '5px',
                    marginTop: '10px',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#3D3C42',
                      fontSize: '15px',
                      fontWeight: 600,
                    }}
                  >
                    Required Passing Score
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <Box>
                      <AppFormikTextFieldNumber
                        name={`${settingsPath}.passingScore`}
                        placeholder="1"
                        type="number"
                        sx={{
                          '& input[type=number]::-webkit-outer-spin-button': {
                            WebkitAppearance: 'inner-spin-button',
                            opacity: 1,
                            display: 'block',
                          },
                          '& input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'inner-spin-button',
                            opacity: 1,
                            display: 'block',
                          },
                          '& input[type=number]': {
                            MozAppearance: 'number-input',
                          },
                          background: '#F1F5F4',
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        color: '#7E7E7E',
                        fontSize: '13px',
                        width: '100%',
                      }}
                    >
                      {
                        values.training_definition[activeStepIndex].questions
                          .length
                      }{' '}
                      of questions correct
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: '12px', color: '#7E7E7E' }}>
                    Learners must answer at least 0 correctly to continue
                  </Typography>
                </Box>

                {/* Show correct */}
                <Box
                  sx={{
                    background: '#F1F5F46B',
                    padding: '5px',
                    borderRadius: '5px',
                    marginTop: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '14px' }}>
                      Show Correct Answers
                    </Typography>

                    <Typography sx={{ fontSize: '12px', color: '#7E7E7E' }}>
                      Display the correct answers after submission
                    </Typography>
                  </Box>

                  <Box
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      setFieldValue(
                        `${settingsPath}.showCorrectAnswers`,
                        !settings?.showCorrectAnswers
                      )
                    }
                  >
                    {settings?.showCorrectAnswers ? (
                      <SwitchActive />
                    ) : (
                      <SwitchNotActive />
                    )}
                  </Box>
                </Box>

                {/* Show explanations */}
                <Box
                  sx={{
                    background: '#F1F5F46B',
                    padding: '5px',
                    borderRadius: '5px',
                    marginTop: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '14px' }}>
                      Show Explanations
                    </Typography>

                    <Typography sx={{ fontSize: '12px', color: '#7E7E7E' }}>
                      Show explanation text for incorrect answers
                    </Typography>
                  </Box>

                  <Box
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      setFieldValue(
                        `${settingsPath}.showExplanations`,
                        !settings?.showExplanations
                      )
                    }
                  >
                    {settings?.showExplanations ? (
                      <SwitchActive />
                    ) : (
                      <SwitchNotActive />
                    )}
                  </Box>
                </Box>

                {/* Randomize answer */}
                <Box
                  sx={{
                    background: '#F1F5F46B',
                    padding: '5px',
                    borderRadius: '5px',
                    marginTop: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '14px' }}>
                      Randomize Answer Options
                    </Typography>

                    <Typography sx={{ fontSize: '12px', color: '#7E7E7E' }}>
                      Shuffle the order of answer choices for each question
                    </Typography>
                  </Box>

                  <Box
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      setFieldValue(
                        `${settingsPath}.randomizeAnswers`,
                        !settings?.randomizeAnswers
                      )
                    }
                  >
                    {settings?.randomizeAnswers ? (
                      <SwitchActive />
                    ) : (
                      <SwitchNotActive />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* TEXT */}
          {activeStep && activeStep.type === 'text' && (
            <Box sx={{ padding: '10px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  paddingBottom: '20px',
                  borderBottom: '1px solid #D1F27A',
                }}
              >
                <QuestionLarge />
                <Box>
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                      Step {parseFloat(activeStep.index) + 1}
                    </Typography>
                    <Box
                      sx={{
                        background: '#052921',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: 20,
                        fontSize: 12,
                      }}
                    >
                      Text
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 14, color: '#3D3C42' }}>
                    {activeStep.title}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ paddingTop: '20px' }}>
                <AppFormikTextField
                  name={`training_definition[${activeStepIndex}].title`}
                  placeholder={activeStep.title || 'Enter title'}
                  outerLabel
                  label="Step title (optional)"
                />
                <Box>
                  <Typography mb={3}>Content</Typography>
                  <ReactQuill
                    theme="snow"
                    value={
                      values.training_definition[activeStepIndex]?.content ?? ''
                    }
                    onChange={(value) => {
                      setFieldValue(
                        `training_definition[${activeStepIndex}].content`,
                        value,
                        true
                      );
                    }}
                  />
                  <Typography
                    sx={{ color: '#A3A3A3', fontSize: '12px' }}
                    mt={2}
                  >
                    Use the toolbar to format text with headings, bold, italic,
                    and links.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {activeStep && activeStep.type === undefined && (
            <Box sx={{ minHeight: '50vh', alignContent: 'center' }}>
              <Typography
                sx={{ fontSize: '16px', color: '#7E7E7E', textAlign: 'center' }}
              >
                Select a step to edit
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TrainingSteps;
